/**
 * VOICE.
 *
 * Proximity chat is not a feature of this game, it is the game. Everything the
 * design leans on — bluffing about which floor you are on, hearing a guard round
 * the corner before you see them, the entire tone of playing this with friends —
 * happens in the two hundred milliseconds between somebody speaking and somebody
 * else hearing it come from the right direction.
 *
 * Which is why speech goes through the same panner every footstep does, rather
 * than through a separate chat overlay. A voice that arrives unpanned is a voice
 * that arrives from nowhere, and it makes the mall stop being a place.
 *
 * Three decisions worth defending:
 *
 * Opus through WebCodecs, not `MediaRecorder`. A recorder gives you a container
 * whose chunks after the first are not independently decodable, so half a second
 * of packet loss loses the rest of the sentence. `AudioEncoder` hands over bare
 * Opus packets, each one standalone, which is exactly what a lossy transport
 * wants.
 *
 * The capture source is injectable. Real players get a microphone; the harness
 * gets a synthesised tone, which means "voice arrives at the far end" is a
 * number a test can assert on rather than something I claim to have heard.
 * `getUserMedia` also simply hangs in headless Chrome, so a test built on it
 * would not exist at all.
 *
 * Nothing in here knows about the network, the world, or where anybody is
 * standing. It emits opaque payloads and asks its owner where to route each
 * speaker. That is what keeps it testable and what stops the audio layer
 * growing a dependency on the netcode.
 */

/**
 * TypeScript's own lib declares this init bag without the one field the spec
 * makes required, so constructing the thing it describes is a type error.
 * Filed upstream repeatedly; augmenting is the standard answer.
 */
declare global {
  interface MediaStreamTrackProcessorInit {
    track: MediaStreamTrack;
  }
}

/**
 * Encoder settings. 20ms frames at 24kbit is around 60 bytes a packet.
 *
 * The rate is the decoder's output rate only — the encoder takes whatever the
 * capture device sends. Opus works at 48kHz internally whatever it is fed, so
 * the two ends never have to agree on a device.
 */
const SAMPLE_RATE = 48000;
const BITRATE = 24000;
const FRAME_US = 20000;

/** Speech is above this and room tone is below it, most of the time. */
const GATE_OPEN = 0.016;
const GATE_SHUT = 0.008;
/** How long the gate stays open after the last loud frame, in seconds. */
const HANGOVER = 0.4;

/**
 * How far ahead of the clock a speaker's audio is scheduled.
 *
 * This is the whole jitter buffer, and it is the one number in this file with a
 * real cost: it is added to every word anybody says. Eighty milliseconds
 * absorbs ordinary wobble on a home connection and is under the threshold where
 * a conversation starts feeling like a phone call.
 */
const JITTER = 0.08;
/** Past this much queued, the speaker is behind and the queue is collapsed. */
const MAX_LEAD = 0.45;
/** A speaker silent this long is torn down. */
const IDLE_OUT = 2.5;

export interface VoiceOptions {
  ctx: AudioContext;
  /**
   * Where this speaker's audio should land — normally a positioned emitter.
   * Returning null drops the frame, which is how a speaker with no body yet is
   * handled without special-casing anything.
   */
  route(id: string): AudioNode | null;
  /** A packet is ready for the wire. The payload is opaque and already copied. */
  onFrame(payload: Uint8Array): void;
}

interface Speaker {
  decoder: AudioDecoder;
  gain: GainNode;
  dest: AudioNode | null;
  /** Context time the next decoded buffer starts at. */
  cursor: number;
  /** Synthetic presentation clock, in microseconds. Advances over dropped frames. */
  stamp: number;
  lastSeq: number;
  level: number;
  lastHeard: number;
  got: number;
  played: number;
  lost: number;
}

export class VoiceChat {
  /** True while the local microphone is actually being encoded and sent. */
  talking = false;
  /** Local input loudness, 0..1. The HUD's talking indicator reads this. */
  level = 0;
  muted = false;

  private readonly ctx: AudioContext;
  private readonly opts: VoiceOptions;
  private readonly speakers = new Map<string, Speaker>();
  private encoder: AudioEncoder | null = null;
  /** Set once the encoder has been told what the source actually sends. */
  private configured = false;
  private track: MediaStreamTrack | null = null;
  private reader: ReadableStreamDefaultReader<AudioData> | null = null;
  private stream: MediaStream | null = null;
  private seq = 0;
  private openUntil = 0;
  private sent = 0;
  private closed = false;

  constructor(opts: VoiceOptions) {
    this.ctx = opts.ctx;
    this.opts = opts;
  }

  /** Everything the browser has to have for any of this to work. */
  static get supported(): boolean {
    return (
      typeof AudioEncoder !== 'undefined' &&
      typeof AudioDecoder !== 'undefined' &&
      typeof MediaStreamTrackProcessor !== 'undefined'
    );
  }

  /**
   * Open the microphone, or whatever is handed in instead of one.
   *
   * Resolves false rather than throwing when there is no permission, no device
   * or no codec. A player who says no to the microphone prompt should get a
   * game with no voice, not a game with no game.
   */
  async start(source?: MediaStream): Promise<boolean> {
    if (this.encoder || this.closed || !VoiceChat.supported) return false;
    try {
      this.stream =
        source ??
        (await navigator.mediaDevices.getUserMedia({
          // All three on, because the alternative is somebody's mechanical
          // keyboard in everybody's ears for eight minutes.
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        }));
      const track = this.stream.getAudioTracks()[0];
      if (!track) return false;
      this.track = track;

      // Deliberately not configured here. See `pump`: what the encoder has to
      // be told depends on what the source turns out to be sending, and that is
      // not knowable until the first frame arrives.
      this.encoder = new AudioEncoder({
        output: (chunk) => this.onEncoded(chunk),
        // A codec that dies mid-round must not take the round with it.
        error: () => this.stop(),
      });

      const processor = new MediaStreamTrackProcessor({ track });
      this.reader = processor.readable.getReader();
      void this.pump();
      return true;
    } catch {
      this.stop();
      return false;
    }
  }

  /** Read raw audio off the track forever, gate it, and feed the encoder. */
  private async pump(): Promise<void> {
    const reader = this.reader;
    if (!reader) return;
    // One buffer, reused. This runs fifty to a hundred times a second and the
    // garbage collector is shared with the render loop.
    let scratch = new Float32Array(0);
    for (;;) {
      let frame: AudioData | undefined;
      try {
        const r = await reader.read();
        if (r.done) break;
        frame = r.value;
      } catch {
        break;
      }
      if (!frame) break;
      if (this.closed || !this.encoder) {
        frame.close();
        break;
      }

      const n = frame.numberOfFrames;
      if (scratch.length < n) scratch = new Float32Array(n);
      try {
        frame.copyTo(scratch.subarray(0, n), { planeIndex: 0, format: 'f32-planar' });
      } catch {
        // Some devices hand over interleaved data the copy refuses to convert.
        // The level meter is worth less than the audio, so send it ungated.
        scratch.fill(GATE_OPEN * 2, 0, n);
      }
      let sum = 0;
      for (let i = 0; i < n; i++) sum += scratch[i] * scratch[i];
      const rms = Math.sqrt(sum / Math.max(1, n));
      this.level = this.level * 0.7 + rms * 0.3;

      const now = this.ctx.currentTime;
      // Hysteresis, not a threshold: one number would chatter the gate on and
      // off inside a single word.
      if (!this.muted && rms > (this.openUntil > now ? GATE_SHUT : GATE_OPEN)) {
        this.openUntil = now + HANGOVER;
      }
      this.talking = !this.muted && this.openUntil > now;

      if (this.talking) {
        /**
         * ENCODE A MONO FRAME WE BUILT, NEVER THE ONE THAT ARRIVED.
         *
         * A microphone is usually one channel and a `MediaStreamDestination` is
         * always two, and the encoder rejects — permanently, closing itself —
         * any frame whose channel count disagrees with its configuration. The
         * failure is `EncodingError: Input audio buffer is incompatible with
         * codec parameters`, it arrives asynchronously on the error callback,
         * and from the outside it looks exactly like a microphone that is not
         * picking anything up. Since the gate has already copied plane zero out
         * to measure it, the mono frame is free.
         *
         * Configuring on the first frame rather than at `start` is the same
         * argument applied to the sample rate: the track decides, not us. Opus
         * is a 48kHz codec internally and resamples whatever it is given, so a
         * 44.1kHz device still decodes correctly at the far end.
         */
        if (!this.configured) {
          this.configured = true;
          this.encoder.configure({
            codec: 'opus',
            sampleRate: frame.sampleRate,
            numberOfChannels: 1,
            bitrate: BITRATE,
          });
        }
        try {
          const mono = new AudioData({
            format: 'f32-planar',
            sampleRate: frame.sampleRate,
            numberOfFrames: n,
            numberOfChannels: 1,
            timestamp: frame.timestamp,
            data: scratch.subarray(0, n),
          });
          this.encoder.encode(mono);
          mono.close();
        } catch {
          /* a dead encoder is handled by its own error callback */
        }
      }
      frame.close();
    }
  }

  private onEncoded(chunk: EncodedAudioChunk): void {
    if (this.closed) return;
    const payload = new Uint8Array(chunk.byteLength);
    chunk.copyTo(payload);
    this.seq = (this.seq + 1) & 0xffff;
    this.sent++;
    this.opts.onFrame(payload);
  }

  /** The sequence number the next outgoing frame will carry. */
  get nextSeq(): number {
    return this.seq;
  }

  // --- listening -----------------------------------------------------------

  /**
   * A packet from somebody else.
   *
   * Called straight off the wire with no buffering of its own: the jitter buffer
   * lives on the playback side, where it can be measured against the audio clock
   * rather than against a frame counter that stalls whenever the tab does.
   */
  receive(id: string, seq: number, payload: Uint8Array): void {
    if (this.closed) return;
    const sp = this.speakerFor(id);
    if (!sp) return;
    sp.got++;
    sp.lastHeard = this.ctx.currentTime;

    // Missing frames are silence, and silence has a duration. Advancing the
    // presentation clock over the gap is what keeps a word after a dropout from
    // being played early and running into the next one.
    const gap = sp.lastSeq < 0 ? 1 : (seq - sp.lastSeq) & 0xffff;
    if (gap === 0 || gap > 200) {
      // A duplicate, or a reorder so old the sentence is over. Both are noise.
      if (gap === 0) return;
      sp.stamp += FRAME_US;
    } else {
      if (gap > 1) sp.lost += gap - 1;
      sp.stamp += FRAME_US * gap;
    }
    sp.lastSeq = seq;

    try {
      // Every Opus packet stands alone, so every chunk is a key frame. Saying
      // 'delta' here makes the decoder wait forever for a key that never comes.
      sp.decoder.decode(
        new EncodedAudioChunk({
          type: 'key',
          timestamp: sp.stamp,
          duration: FRAME_US,
          data: payload,
        }),
      );
    } catch {
      this.drop(id);
    }
  }

  private speakerFor(id: string): Speaker | null {
    const found = this.speakers.get(id);
    if (found) {
      // The route can appear late — a voice frame often beats the snapshot that
      // creates the body it should come out of.
      if (!found.dest) {
        const dest = this.opts.route(id);
        if (dest) {
          found.gain.connect(dest);
          found.dest = dest;
        }
      }
      return found;
    }
    if (!VoiceChat.supported) return null;

    const gain = this.ctx.createGain();
    // Speech carries further than footsteps by design: a shout across the
    // atrium should reach, and the panner's roll-off already handles distance.
    gain.gain.value = 1.6;
    const dest = this.opts.route(id);
    if (dest) gain.connect(dest);

    const sp: Speaker = {
      decoder: null as unknown as AudioDecoder,
      gain,
      dest,
      cursor: 0,
      stamp: 0,
      lastSeq: -1,
      level: 0,
      lastHeard: this.ctx.currentTime,
      got: 0,
      played: 0,
      lost: 0,
    };
    try {
      sp.decoder = new AudioDecoder({
        output: (data) => this.schedule(sp, data),
        error: () => this.drop(id),
      });
      sp.decoder.configure({ codec: 'opus', sampleRate: SAMPLE_RATE, numberOfChannels: 1 });
    } catch {
      return null;
    }
    this.speakers.set(id, sp);
    return sp;
  }

  /** Put one decoded frame on the clock, immediately after the last one. */
  private schedule(sp: Speaker, data: AudioData): void {
    const frames = data.numberOfFrames;
    const channels = data.numberOfChannels;
    let peak = 0;
    try {
      const buf = this.ctx.createBuffer(channels, frames, data.sampleRate);
      for (let c = 0; c < channels; c++) {
        const out = buf.getChannelData(c);
        data.copyTo(out, { planeIndex: c, format: 'f32-planar' });
        for (let i = 0; i < frames; i += 8) peak = Math.max(peak, Math.abs(out[i]));
      }

      const now = this.ctx.currentTime;
      // Two ways the cursor goes wrong, and they need opposite fixes. Behind the
      // clock means the speaker paused and the buffer drained: re-prime it.
      // Too far ahead means frames arrived faster than real time: collapse the
      // queue rather than let latency grow for the rest of the round.
      if (sp.cursor < now + 0.005 || sp.cursor > now + MAX_LEAD) sp.cursor = now + JITTER;

      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(sp.gain);
      src.start(sp.cursor);
      sp.cursor += buf.duration;
      sp.played++;
      sp.level = peak;
    } catch {
      /* a buffer we cannot build is a frame we do not play */
    } finally {
      data.close();
    }
  }

  /**
   * Per-frame housekeeping: fade the talking indicators and forget the quiet.
   *
   * Speakers are torn down rather than left parked because each one holds a
   * hardware-backed Opus decoder, and a full lobby cycling through eight rounds
   * would otherwise accumulate a hundred of them.
   */
  update(): void {
    const now = this.ctx.currentTime;
    if (!this.talking) this.level *= 0.85;
    for (const [id, sp] of this.speakers) {
      sp.level *= 0.85;
      if (now - sp.lastHeard > IDLE_OUT) this.drop(id);
    }
  }

  /** Is this player audibly speaking right now? Drives the HUD's name tags. */
  speaking(id: string): boolean {
    const sp = this.speakers.get(id);
    return !!sp && sp.level > 0.01;
  }

  private drop(id: string): void {
    const sp = this.speakers.get(id);
    if (!sp) return;
    this.speakers.delete(id);
    try {
      if (sp.decoder.state !== 'closed') sp.decoder.close();
    } catch {
      /* already gone */
    }
    sp.gain.disconnect();
  }

  /**
   * What voice is actually doing, as numbers.
   *
   * Same reason the mix has `stats` and every screenshot has a histogram: I
   * cannot hear this. "Voice works" has to mean frames left one machine and
   * frames were played on another, and those are the two counters here.
   */
  get stats(): {
    on: boolean;
    talking: boolean;
    muted: boolean;
    level: number;
    sent: number;
    speakers: Array<{ id: string; got: number; played: number; lost: number; level: number; routed: boolean }>;
  } {
    return {
      on: this.encoder !== null,
      talking: this.talking,
      muted: this.muted,
      level: Number(this.level.toFixed(4)),
      sent: this.sent,
      speakers: [...this.speakers].map(([id, sp]) => ({
        id,
        got: sp.got,
        played: sp.played,
        lost: sp.lost,
        level: Number(sp.level.toFixed(4)),
        routed: sp.dest !== null,
      })),
    };
  }

  stop(): void {
    this.reader?.cancel().catch(() => {});
    this.reader = null;
    try {
      if (this.encoder && this.encoder.state !== 'closed') this.encoder.close();
    } catch {
      /* already gone */
    }
    this.encoder = null;
    this.configured = false;
    this.track?.stop();
    this.track = null;
    // Only tracks this class opened are stopped wholesale; an injected stream
    // belongs to whoever made it.
    this.stream = null;
    this.talking = false;
    this.level = 0;
  }

  dispose(): void {
    this.closed = true;
    this.stop();
    for (const id of [...this.speakers.keys()]) this.drop(id);
  }
}
